/**
 * Sales.jsx — Aba "Vendas" do DetailPanel.
 *
 * Comportamento dependente do `presetIdentifier`:
 *
 *   - identifier='sku': mostra KPIs gerais (somatórios do SKU) + breakdown
 *     por anúncio (cada MLB do SKU como uma linha tipo mini-DataTable com
 *     hoje/7d/30d). Útil pra ver "qual anúncio vendeu mais".
 *
 *   - identifier='mlb': mostra só os dados do MLB selecionado. Sem breakdown
 *     (a row JÁ é o MLB), evita confusão.
 *
 * Charts (Recharts, simplificados pra um único SKU/MLB):
 *   1. Receita diária (LineChart): a partir de row.dailyByDate.
 *   2. Top 5 MLBs do SKU por sales30 (BarChart vertical) — só em modo SKU.
 *   3. Breakdown de status do pedido (PieChart): paid/cancelled/other
 *      a partir de row.orderTotals.
 */
import { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { EnumerateDates } from '../../../utils/dateUtils';
import { TKPI } from '../../primitives';
import { useDetailPanel } from '../index.jsx';
import { StatusMsg } from '../internals.jsx';

// Paleta consistente com SelloutDashboard (mesma ordem).
const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const STATUS_COLOR = {
    paid: '#10b981',
    cancelled: '#ef4444',
    other: '#f59e0b',
};

const STATUS_LABEL = {
    paid: 'Pagos',
    cancelled: 'Cancelados',
    other: 'Outros',
};

/**
 * Converte `row.dailyByDate` (Map<isoDate, qty>) em série diária ordenada
 * pra LineChart. Cada ponto: { date: '23/05', qty: N, isoDate: '2026-05-23' }.
 *
 * UTC PURO — formata DD/MM via string slicing do ISO (`split('-')`). Sem
 * `new Date(iso)` + `toLocaleDateString`, que aplica fuso do browser e
 * provoca off-by-1 day no display.
 *
 * Se `periodStartIso`/`periodEndIso` são informados, CLIPA a série pra esse
 * intervalo (caso contrário usa todo o range do snapshot — 90d).
 */
function buildDailySeries(row, periodStartIso = null, periodEndIso = null) {
    if (!row.dailyByDate || !(row.dailyByDate instanceof Map || typeof row.dailyByDate === 'object')) {
        return [];
    }
    const entries =
        row.dailyByDate instanceof Map ? Array.from(row.dailyByDate.entries()) : Object.entries(row.dailyByDate);

    // Normaliza chaves p/ YYYY-MM-DD (defensivo contra timestamps) e soma
    // quantidades caídas no mesmo dia.
    const byDay = new Map();
    for (const [k, qty] of entries) {
        const day = String(k).slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) continue;
        byDay.set(day, (byDay.get(day) || 0) + Number(qty || 0));
    }

    // Range a plotar: período ativo (clip) quando informado; senão min..max
    // das datas presentes. EnumerateDates devolve TODOS os dias do intervalo —
    // dias SEM venda entram como qty=0 em vez de serem OMITIDOS (antes o gráfico
    // "pulava" dias zerados, conectando datas não-adjacentes e distorcendo a
    // tendência diária real).
    const presentDays = Array.from(byDay.keys()).sort();
    if (presentDays.length === 0 && !(periodStartIso && periodEndIso)) return [];
    const startIso = periodStartIso || presentDays[0];
    const endIso = periodEndIso || presentDays[presentDays.length - 1];
    if (!startIso || !endIso || startIso > endIso) return [];

    return EnumerateDates(startIso, endIso).map((iso) => {
        const parts = iso.split('-');
        const display = parts.length === 3 ? `${parts[2]}/${parts[1]}` : iso;
        return {
            isoDate: iso,
            date: display,
            qty: Number(byDay.get(iso) || 0),
        };
    });
}

/**
 * Constrói os dados pro PieChart de status — entrada: row.orderTotals
 *   { paid, cancelled, other } — todos inteiros. Filtra zeros pra não
 *   poluir a legenda do gráfico.
 */
function buildStatusPieData(orderTotals) {
    if (!orderTotals) return [];
    return [
        { name: STATUS_LABEL.paid, key: 'paid', value: Number(orderTotals.paid || 0) },
        { name: STATUS_LABEL.cancelled, key: 'cancelled', value: Number(orderTotals.cancelled || 0) },
        { name: STATUS_LABEL.other, key: 'other', value: Number(orderTotals.other || 0) },
    ].filter((s) => s.value > 0);
}

/** Header padrão de seção (label uppercase letterspaced). */
function SectionLabel({ T, children }) {
    return (
        <div
            style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                color: T.muted,
                marginBottom: 8,
            }}
        >
            {children}
        </div>
    );
}

/** Card visual com border + bg (engloba gráfico). */
function ChartCard({ T, children }) {
    return (
        <div
            style={{
                border: `1px solid ${T.line}`,
                borderRadius: 4,
                padding: 12,
                background: T.bgSoft,
                marginBottom: 16,
            }}
        >
            {children}
        </div>
    );
}

/**
 * Mini-tabela de anúncios do mesmo SKU — visual igual à DataTable do
 * /operacoes (header bgMid, rows alternando, border-right entre cells).
 * Colunas: MLB | Título | Hoje | 7d | 30d.
 */
function MlbBreakdown({ mlbs, T }) {
    const GRID = '110px 1fr 60px 60px 70px';
    // Sort por vendas 30d descendente — mais vendido em cima.
    const sorted = [...mlbs].sort((a, b) => (b.sales30 || 0) - (a.sales30 || 0));
    return (
        <div>
            <SectionLabel T={T}>Anúncios vinculados ({mlbs.length})</SectionLabel>
            <div
                style={{
                    border: `1px solid ${T.line}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: GRID,
                        background: T.bgMid,
                        borderBottom: `1px solid ${T.lineMid}`,
                    }}
                >
                    {['MLB', 'Título', 'Hoje', '7d', '30d'].map((label, i) => (
                        <div
                            key={label}
                            style={{
                                padding: '6px 8px',
                                fontSize: 10,
                                fontWeight: 600,
                                color: T.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                                textAlign: i >= 2 ? 'right' : 'left',
                                borderRight: i < 4 ? `1px solid ${T.line}` : 'none',
                            }}
                        >
                            {label}
                        </div>
                    ))}
                </div>
                {sorted.map((mlb, idx) => (
                    <div
                        key={mlb.mlb}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: GRID,
                            background: idx % 2 === 0 ? T.bg : T.bgSoft,
                            borderBottom: idx < sorted.length - 1 ? `1px solid ${T.line}` : 'none',
                            alignItems: 'center',
                            minHeight: 36,
                        }}
                    >
                        <div
                            style={{
                                padding: '0 8px',
                                fontFamily: T.mono,
                                fontSize: 11,
                                color: T.muted,
                                borderRight: `1px solid ${T.line}`,
                            }}
                        >
                            {mlb.mlb}
                        </div>
                        <div
                            style={{
                                padding: '0 8px',
                                fontSize: 12,
                                color: T.ink,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                borderRight: `1px solid ${T.line}`,
                                textTransform: 'uppercase',
                            }}
                            title={mlb.name}
                        >
                            {mlb.name}
                        </div>
                        <div
                            style={{
                                padding: '0 8px',
                                fontFamily: T.mono,
                                fontSize: 12,
                                textAlign: 'right',
                                color: (mlb.sales_today || 0) === 0 ? 'rgb(255,120,120)' : T.ink,
                                borderRight: `1px solid ${T.line}`,
                            }}
                        >
                            {mlb.sales_today || 0}
                        </div>
                        <div
                            style={{
                                padding: '0 8px',
                                fontFamily: T.mono,
                                fontSize: 12,
                                textAlign: 'right',
                                color: (mlb.sales7 || 0) === 0 ? 'rgb(255,120,120)' : T.ink,
                                borderRight: `1px solid ${T.line}`,
                            }}
                        >
                            {mlb.sales7 || 0}
                        </div>
                        <div
                            style={{
                                padding: '0 8px',
                                fontFamily: T.mono,
                                fontSize: 12,
                                textAlign: 'right',
                                color: (mlb.sales30 || 0) === 0 ? 'rgb(255,120,120)' : T.ink,
                            }}
                        >
                            {mlb.sales30 || 0}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * LineChart de qty diária. Lê dailyByDate (Map) ou daily30 (fallback).
 *
 * Diferença vs SelloutDashboard: aqui não temos `total_amount`, só
 * quantidade diária do SKU/MLB. Por isso é "Vendas diárias (un.)" e não
 * "Receita".
 */
function DailySalesChart({ row, T, periodStartIso, periodEndIso }) {
    const series = useMemo(
        () => buildDailySeries(row, periodStartIso, periodEndIso),
        [row, periodStartIso, periodEndIso],
    );
    if (series.length === 0) {
        return (
            <ChartCard T={T}>
                <SectionLabel T={T}>Vendas diárias</SectionLabel>
                <StatusMsg T={T}>Sem dados diários no período.</StatusMsg>
            </ChartCard>
        );
    }
    return (
        <ChartCard T={T}>
            <SectionLabel T={T}>Vendas diárias (un.)</SectionLabel>
            <ResponsiveContainer width="100%" height={220}>
                <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                    <XAxis
                        dataKey="date"
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        // Mostra só ~6 labels pra não poluir em séries longas.
                        interval={Math.max(0, Math.floor(series.length / 6) - 1)}
                    />
                    <YAxis
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        width={32}
                        allowDecimals={false}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: T.bg,
                            border: `1px solid ${T.line}`,
                            borderRadius: 4,
                            color: T.ink,
                            fontSize: 11,
                        }}
                        labelStyle={{ color: T.ink, fontWeight: 600 }}
                        formatter={(value) => [`${value} un.`, 'Vendas']}
                    />
                    <Line
                        type="monotone"
                        dataKey="qty"
                        stroke={CHART_COLORS[0]}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLORS[0], r: 2 }}
                        activeDot={{ r: 4 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}

/**
 * BarChart vertical (horizontal bars) com top 5 MLBs por sales30 do SKU.
 * Só faz sentido em modo SKU (modo MLB já é uma única row).
 */
function TopMlbsChart({ mlbs, T }) {
    const data = useMemo(() => {
        return [...mlbs]
            .sort((a, b) => (b.sales30 || 0) - (a.sales30 || 0))
            .slice(0, 5)
            .map((m) => ({
                // Trunca pra ficar legível na coluna Y.
                name: m.mlb || '—',
                fullName: m.name || m.mlb,
                sales30: Number(m.sales30 || 0),
            }));
    }, [mlbs]);

    if (data.length === 0) {
        return null;
    }

    return (
        <ChartCard T={T}>
            <SectionLabel T={T}>Top {data.length} anúncios (30d)</SectionLabel>
            <ResponsiveContainer width="100%" height={Math.max(120, data.length * 36)}>
                <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                    <XAxis
                        type="number"
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        allowDecimals={false}
                    />
                    <YAxis
                        type="category"
                        dataKey="name"
                        stroke={T.muted}
                        style={{ fontSize: 10, fontFamily: T.mono }}
                        width={110}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: T.bg,
                            border: `1px solid ${T.line}`,
                            borderRadius: 4,
                            color: T.ink,
                            fontSize: 11,
                        }}
                        labelFormatter={(label, payload) => {
                            if (payload?.[0]?.payload?.fullName) {
                                return payload[0].payload.fullName;
                            }
                            return label;
                        }}
                        formatter={(value) => [`${value} un.`, 'Vendas 30d']}
                    />
                    <Bar dataKey="sales30" fill={CHART_COLORS[1]} />
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}

/**
 * PieChart status — paid (verde), cancelled (vermelho), other (amarelo).
 * Lê row.orderTotals. Se zerado, mostra mensagem.
 */
function OrderStatusChart({ orderTotals, T }) {
    const data = useMemo(() => buildStatusPieData(orderTotals), [orderTotals]);
    if (data.length === 0) {
        return (
            <ChartCard T={T}>
                <SectionLabel T={T}>Status dos pedidos</SectionLabel>
                <StatusMsg T={T}>Sem totalizadores de status disponíveis.</StatusMsg>
            </ChartCard>
        );
    }
    const totalOrders = data.reduce((s, d) => s + d.value, 0);
    return (
        <ChartCard T={T}>
            <SectionLabel T={T}>Status dos pedidos ({totalOrders})</SectionLabel>
            <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={70}
                        dataKey="value"
                    >
                        {data.map((entry) => (
                            <Cell key={`cell-${entry.key}`} fill={STATUS_COLOR[entry.key]} />
                        ))}
                    </Pie>
                    <Tooltip
                        contentStyle={{
                            backgroundColor: T.bg,
                            border: `1px solid ${T.line}`,
                            borderRadius: 4,
                            color: T.ink,
                            fontSize: 11,
                        }}
                        formatter={(value, _name, props) => [`${value} pedidos`, props?.payload?.name]}
                    />
                </PieChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}

export function Sales() {
    const { row, T, tab, presetIdentifier, allMlbRows, periodStartIso, periodEndIso } = useDetailPanel();
    if (tab !== 'vendas') return null;

    const isMlbMode = presetIdentifier === 'mlb';
    // Em modo SKU: pega TODOS os mlbRows com o mesmo sku. Em modo inventory:
    // só os anúncios DESSE inventory (row.mlbs) — o SKU pode ter outros
    // inventories. Em modo MLB: lista só com o próprio row (pra unificar
    // render abaixo, sem branches).
    const mlbsOfSku = isMlbMode
        ? []
        : Array.isArray(row.skus)
          ? allMlbRows.filter((r) => row.mlbs.includes(r.mlb))
          : allMlbRows.filter((r) => r.sku === row.sku);

    // KPIs gerais — em modo SKU, somam todos os MLBs (já é o caso porque
    // row.sales* foi agregado). Em modo MLB, são só do anúncio.
    return (
        <div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 8,
                    marginBottom: 16,
                }}
            >
                <TKPI label="Hoje" value={row.sales_today || 0} mini />
                <TKPI label="Ontem" value={row.sales_yday || 0} mini />
                <TKPI label="7 dias" value={row.sales7 || 0} mini />
                <TKPI label="30 dias" value={row.sales30 || 0} mini />
            </div>

            {/* Tabela de métricas curtas (média/melhor) — referência rápida, vale
                pra ambos os modos. */}
            <div
                style={{
                    border: `1px solid ${T.line}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                    marginBottom: 16,
                }}
            >
                {[
                    ['Média/dia 7d', `${(row.avg7 || 0).toFixed(1)} un`],
                    ['Melhor dia 7d', `${row.best7 || 0} un`],
                ].map(([k, v], i) => (
                    <div
                        key={k}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            fontSize: 12,
                            borderBottom: i < 1 ? `1px solid ${T.line}` : 'none',
                            background: i % 2 === 0 ? T.bg : T.bgSoft,
                        }}
                    >
                        <span style={{ color: T.muted }}>{k}</span>
                        <span style={{ fontFamily: T.mono, fontWeight: 600, color: T.ink }}>{v}</span>
                    </div>
                ))}
            </div>

            {/* Charts (Recharts) — receita diária, top anúncios e status */}
            <DailySalesChart row={row} T={T} periodStartIso={periodStartIso} periodEndIso={periodEndIso} />

            {/* Top MLBs só em modo SKU com >0 anúncios */}
            {!isMlbMode && mlbsOfSku.length > 0 && <TopMlbsChart mlbs={mlbsOfSku} T={T} />}

            <OrderStatusChart orderTotals={row.orderTotals} T={T} />

            {/* Breakdown por anúncio: SÓ em modo SKU e quando há +1 MLB.
                Mostra cada MLB com Hoje/7d/30d e total — user vê qual
                anúncio está puxando mais venda dentro do mesmo produto. */}
            {!isMlbMode && mlbsOfSku.length > 0 && <MlbBreakdown mlbs={mlbsOfSku} T={T} />}

            {!isMlbMode && mlbsOfSku.length === 0 && (
                <StatusMsg T={T}>Sem anúncios com vendas no período (90d).</StatusMsg>
            )}
        </div>
    );
}
